import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, ArrowRight, Star, Quote, ChevronLeft, ChevronRight, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import BackgroundPattern from "@/components/BackgroundPattern";
import EstimateCalculator from "@/components/EstimateCalculator";

interface Testimonial {
  name: string;
  location: string;
  text: string;
}

interface ServiceFeature {
  title: string;
  description: string;
}

interface ServicePageProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  canonicalPath: string;
  heroImage: string;
  heroAlt: string;
  headline: string;
  subheadline: string;
  introText: string;
  features: ServiceFeature[];
  processSteps: { step: string; title: string; description: string }[];
  testimonials: Testimonial[];
  serviceValue: string;
  ctaText?: string;
}

const ServicePageLayout = ({
  title,
  metaTitle,
  metaDescription,
  canonicalPath,
  heroImage,
  heroAlt,
  headline,
  subheadline,
  introText,
  features,
  processSteps,
  testimonials,
  serviceValue,
}: ServicePageProps) => {
  const [tIdx, setTIdx] = useState(0);
  const t = testimonials[tIdx];

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://zenithsolutionsnm.com${canonicalPath}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://zenithsolutionsnm.com${canonicalPath}`} />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <img src={heroImage} alt={heroAlt} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 container mx-auto px-4 py-32 text-center max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-sm font-bold uppercase tracking-widest text-white/70 mb-4">{title}</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-[1.1] text-white mb-6 drop-shadow-lg">
              {headline}
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-8 drop-shadow-md">{subheadline}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:5056048536">
                <Button
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 text-base font-bold h-14 px-8 gap-2 shadow-lg w-full sm:w-auto"
                >
                  <Phone className="h-5 w-5" /> (505) 264-0170
                </Button>
              </a>
              <Link to="/get-estimate">
                <Button
                  size="lg"
                  className="bg-white/15 border-2 border-white/40 text-white hover:bg-white/25 hover:text-white text-base font-bold h-14 px-8 gap-2 w-full sm:w-auto backdrop-blur-sm"
                >
                  Get Free Estimate <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro + Features */}
      <section className="section-padding bg-section-light">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-black text-foreground mb-6">What Sets Us Apart</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">{introText}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card border rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <span className="text-primary font-black text-lg">{i + 1}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-primary text-primary-foreground relative">
        <BackgroundPattern variant="tealprint" className="text-primary-foreground" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-12">
            <p className="text-sm font-bold uppercase tracking-widest text-primary-foreground/60 mb-3">Our Process</p>
            <h2 className="text-3xl md:text-4xl font-black">How It Works</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-primary-foreground/5 border border-primary-foreground/15 rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-full bg-primary-foreground/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-black text-primary-foreground">{s.step}</span>
                </div>
                <h3 className="font-bold text-primary-foreground mb-2">{s.title}</h3>
                <p className="text-primary-foreground/70 text-sm">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-section-mid relative">
        <BackgroundPattern variant="roofline" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">Client Reviews</p>
          <h2 className="text-3xl md:text-4xl font-black text-foreground mb-12">What Our {title} Clients Say</h2>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={tIdx}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="bg-card border rounded-2xl p-8 md:p-12 shadow-lg"
              >
                <Quote className="h-10 w-10 text-primary mx-auto mb-6 opacity-50" />
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                  ))}
                </div>
                <blockquote className="text-lg md:text-xl text-foreground leading-relaxed mb-6 max-w-2xl mx-auto">
                  "{t.text}"
                </blockquote>
                <p className="font-bold text-foreground">{t.name}</p>
                <p className="text-sm text-muted-foreground">{t.location}</p>
              </motion.div>
            </AnimatePresence>
            {testimonials.length > 1 && (
              <div className="flex justify-center gap-3 mt-8">
                <button
                  onClick={() => setTIdx((i) => (i - 1 + testimonials.length) % testimonials.length)}
                  className="p-2.5 rounded-full border bg-card hover:bg-muted transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setTIdx(i)}
                      className={`w-6 h-6 rounded-full transition-colors flex items-center justify-center ${i === tIdx ? "bg-primary" : "bg-border"}`}
                      style={{ backgroundClip: 'content-box', padding: '7px' }}
                      aria-label={`Testimonial ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setTIdx((i) => (i + 1) % testimonials.length)}
                  className="p-2.5 rounded-full border bg-card hover:bg-muted transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call CTA + Estimate Calculator side-by-side */}
      <section className="section-padding bg-foreground text-white relative">
        <BackgroundPattern variant="darkgrid" className="text-white" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Left: CTA */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-white/50 mb-3">Get Started Today</p>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Ready for Your {title} Project?</h2>
                <p className="text-white/70 leading-relaxed">
                  Call us for a free on-site inspection and exact quote, or use our instant calculator to get an
                  estimated price range right now.
                </p>
              </div>

              <div className="space-y-4">
                <a href="tel:5056048536" className="block">
                  <Button
                    size="lg"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-16 px-10 gap-3 shadow-xl text-lg w-full sm:w-auto"
                  >
                    <Phone className="h-6 w-6" /> (505) 264-0170
                  </Button>
                </a>
                <Link to="/get-estimate" className="block">
                  <Button
                    size="lg"
                    className="bg-white/10 border-2 border-white/30 text-white hover:bg-white/20 hover:text-white font-bold h-14 px-8 gap-2 w-full sm:w-auto"
                  >
                    Fill Out Quote Form <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  <Calculator className="h-4 w-4" /> Don't want to call?
                </p>
                <p className="text-xs text-white/60 mt-1">Get an instant online estimate using our calculator →</p>
              </div>
            </motion.div>

            {/* Right: Estimate Calculator */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <EstimateCalculator defaultService={serviceValue as any} compact />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Back to home */}
      <section className="py-12 bg-section-light text-center">
        <div className="container mx-auto px-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
      </section>

      <FooterSection />
    </>
  );
};

export default ServicePageLayout;
